import { IconKeypad } from '@/assets/static/icons';
import { useLayoutContext } from '@/container/context/layout/layout-context';
import { useworkspaceAuthMeV2Query } from '@/types/generated/graphql';
import { ProfileDropdown } from '@components/profile-dropdown/components/profile-dropdown';
import { KYCHeaderBadge } from '@components/rate-limit/kyc-header-badge';
import { ConfirmPaymentHeaderBadge } from '@components/confirm-payment/confirm-payment-header-badge';
import { DesignSwitchBadge } from '@modules/design-switch/design-switch.badge';
import { NotificationPage } from '@modules/notifications/notification.page';
import { useWaitingCallsCount } from '@modules/phone/features/queued-calls/hooks/use-waiting-calls-count';
import { revealQueuePanel } from '@modules/phone/features/queued-calls/queued-calls.helpers';
import { usePhone } from '@modules/phone/hooks/use-phone';
import { useDeviceStore } from '@modules/phone/store/device.store';
import { usePhoneStore } from '@modules/phone/store/phone.store';
import { COOKIE_KEY, cacheOnly } from '@shared/constants';
import { useScreenSize } from '@shared/hooks/use-screen';
import { useSupportChat } from '@shared/hooks/use-support-chat';
import { useSupportChatStore } from '@shared/store/support-chat.store';
import { capitalize } from '@shared/utils/capitalize';
import { Badge, Button, Divider, Tooltip } from 'antd';
import { Header } from 'antd/es/layout/layout';
import clsx from 'clsx';
import Cookies from 'js-cookie';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineMenuOpen } from 'react-icons/md';
import { RiCustomerService2Fill } from 'react-icons/ri';

function AppHeader() {
  const { data: wksData, loading } = useworkspaceAuthMeV2Query(cacheOnly);
  const { isSmallerDevice } = useScreenSize();
  const { openSidebarMobileDrawer } = useLayoutContext();
  const { openDialer, isOpen } = usePhoneStore();
  const { closePhone } = usePhone();
  const deviceStore = useDeviceStore();
  const accessToken = Cookies.get(COOKIE_KEY.accessToken);
  const { t } = useTranslation();
  const { openSupportChat, closeSupportChat } = useSupportChat();

  const { incomingMessageExists, isSupportChatOpen } = useSupportChatStore();
  const isDisabled = !!deviceStore?.call;
  const isPermanentMigrated = loading || !!wksData?.workspaceAuthMeV2?.data?.v2MigratedAt;
  const isDemo = window.location.pathname.startsWith('/demo');
  const waitingCallsCount = useWaitingCallsCount({ skip: isDemo });

  const dialer = (
    <Button
      type="text"
      shape="circle"
      data-dialer-toggle
      disabled={isDisabled}
      onClick={() => {
        if (isDisabled) return;
        if (isOpen) {
          closePhone();
        } else if (waitingCallsCount > 0) {
          revealQueuePanel();
        } else {
          openDialer();
        }
      }}
      className={clsx(
        'border! size-9! min-w-9! flex items-center justify-center border-gray-500! transition-all duration-300 cursor-pointer group',
        isOpen ? 'bg-primary-100!' : 'hover:bg-gray-200!'
      )}
    >
      <IconKeypad className="transition-transform duration-500 group-hover:rotate-y-180 text-primary" />
    </Button>
  );

  const dialerWithWaitingCalls = waitingCallsCount ? (
    <Tooltip title={t('callers_waiting_in_queue_count', { count: waitingCallsCount })}>
      <Badge count={waitingCallsCount} size="default" overflowCount={99} offset={[-2, 8]}>
        {dialer}
      </Badge>
    </Tooltip>
  ) : (
    dialer
  );

  const supportChat = (
    <Tooltip title={capitalize(t('contact_support'))}>
      <Button
        className={clsx(
          'rounded-full! p-2! cursor-pointer transition-all! duration-300 hover:bg-gray-200! ml-2!',
          isSupportChatOpen ? 'bg-primary-100!' : 'hover:bg-gray-200'
        )}
        onClick={() => {
          if (isSupportChatOpen) {
            closeSupportChat();
          } else {
            openSupportChat();
          }
        }}
        type="link"
      >
        <Badge count={incomingMessageExists}>
          <RiCustomerService2Fill size={20} className="text-text-800 cursor-pointer" />
        </Badge>
      </Button>
    </Tooltip>
  );

  return (
    <Header className="bg-surface! h-14! border-y border-gray-300 px-4! sticky top-0 w-full flex items-center justify-between z-50">
      <div className="flex items-center gap-2">
        {isSmallerDevice && (
          <Button data-sidebar-toggle className="px-2! rounded-full!" onClick={openSidebarMobileDrawer}>
            <MdOutlineMenuOpen size={24} color="var(--color-primary-400)" />
          </Button>
        )}
      </div>
      <div className="flex gap-3 items-center overflow-x-auto overflow-y-hidden">
        <ConfirmPaymentHeaderBadge />
        <KYCHeaderBadge />
        <div className="flex items-center">
          {isPermanentMigrated || isDemo ? null : <DesignSwitchBadge />}
          {!isDemo && supportChat}
          <Divider type="vertical" className="mx-1!" />
          <span data-demo-interactive className="size-9 flex items-center justify-center">
            <NotificationPage />
          </span>
        </div>
        {dialerWithWaitingCalls}
        {accessToken && <ProfileDropdown />}
      </div>
    </Header>
  );
}

export default memo(AppHeader);
